import { UserAdhesionDetailsI } from '../../api/users/interfaces/UserAdhesionDetailsI';
import {
	UserType,
	calculatePensionPlanMetrics,
	calculateTotalSaved
} from '../pensionPlanCalculations';
import { TipoPlanResumen } from './planes';

/** Rentabilidad acumulada (desde el inicio del plan), en € y en % sobre `base`. */
export interface Rentabilidad {
	ganancia: number;
	/** El denominador del %: lo metido (aportado más traspasado), como Caser (product#47). */
	base: number;
	porcentaje: number;
}

export interface EntradaRentabilidad {
	tipo: TipoPlanResumen;
	/** `get-adhesion-details` de ESTE plan (`mode` + `userEppConfigId`). */
	adhesionDetails: UserAdhesionDetailsI[];
	/** De sus movimientos (`resumenDeMovimientos`), no de `user-cost-basis`: ver allí. */
	costeTraspasos: number;
	rescatadoTotal: number;
}

/**
 * La rentabilidad acumulada de UN plan, con `calculatePensionPlanMetrics`: la misma cuenta que
 * Inicio y Tu beneficio, para que el Resumen y el detalle del plan no digan cosas distintas.
 *
 * Sin la lista de traspasos (`transfers: []`): esa lista es del usuario, no del plan, y su filtro
 * es por tipología, así que a quien tiene dos planes individuales le mezclaría los traspasos de
 * los dos. El traspaso entra como `externalTransferCostBasis`, sacado de los movimientos de ESTE
 * plan.
 *
 * `null` si el dato no es fiable (sin saldo, o un % fuera de ±80 %, que suele ser un traspaso sin
 * registrar): «sin dato», nunca un 0 % ni una ganancia inventada.
 */
export function rentabilidadDelPlan({
	tipo,
	adhesionDetails,
	costeTraspasos,
	rescatadoTotal
}: EntradaRentabilidad): Rentabilidad | null {
	const metricas = calculatePensionPlanMetrics({
		adhesionDetails,
		userType: tipo === 'EMPLEO' ? UserType.COMPANY : UserType.INDIVIDUAL,
		transfers: [],
		externalTransferCostBasis: costeTraspasos,
		totalRescued: rescatadoTotal
	});
	if (!metricas.isPerformanceReliable) return null;
	return {
		ganancia: metricas.performance.amount,
		// Mismo denominador que `calculatePensionPlanMetrics`: lo metido, aportado más traspasado
		// (product#47). Así el agregado (Σ ganancia / Σ base) también es sobre lo metido.
		base: metricas.totalContributions + costeTraspasos,
		porcentaje: metricas.performance.percentage
	};
}

/** Más allá, un % del año no es creíble: suele faltar un movimiento (mismo listón que Inicio). */
export const MAXIMO_CREIBLE = 80;

export interface EntradaRentabilidadDelAnio {
	/** `get-adhesion-details` de ESTE plan: lo que vale hoy. */
	adhesionDetails: UserAdhesionDetailsI[];
	/** Lo que valía el 1 de enero (`vestedRightsAtYearStartCents`). */
	saldoInicioAnio: number | null;
	/** Lo que entró menos lo que salió este año (`resumenDeMovimientos`). */
	flujoNetoAnio: number;
}

/**
 * La rentabilidad del AÑO EN CURSO de UN plan, la que enseña su card («Rentabilidad 2026», como
 * en Proa): lo que vale hoy, menos lo que valía el 1 de enero y lo que entró este año (lo que
 * salió suma: un rescate no es pérdida). El % es sobre lo que valía más lo que entró.
 *
 * Simple, sin ponderar por fechas: una aportación de octubre cuenta en la base igual que una de
 * enero, así que con aportaciones el % se queda algo corto, nunca inflado.
 *
 * `null` si falta el saldo del 1 de enero o el de hoy, si no hay base (nunca un NaN), o si sale
 * fuera de ±80 %:
 * «sin dato», nunca un 0 % (con un 0 de partida, todo el saldo sería ganancia).
 */
export function rentabilidadDelAnio({
	adhesionDetails,
	saldoInicioAnio,
	flujoNetoAnio
}: EntradaRentabilidadDelAnio): Rentabilidad | null {
	// Sin adhesión no se sabe lo que vale hoy. Con ella, 0 € es un dato: el plan se vació.
	if (saldoInicioAnio === null || adhesionDetails.length === 0) return null;
	const saldoHoy = calculateTotalSaved(
		adhesionDetails,
		adhesionDetails.reduce((total, fila) => total + fila.vestedRights, 0)
	);
	return rentabilidadEntreSaldos({
		saldo: saldoHoy,
		saldoInicial: saldoInicioAnio,
		flujoNeto: flujoNetoAnio
	});
}

/**
 * La cuenta de `rentabilidadDelAnio` con el saldo ya sabido: lo que vale, menos lo que valía al
 * empezar y lo que entró (lo que salió suma), sobre lo que valía más lo que entró. La usa también
 * la gráfica de evolución, mes a mes, para que su último punto y la card no digan cosas distintas.
 *
 * `null` sin base (nunca un NaN) o fuera de ±80 %.
 */
export function rentabilidadEntreSaldos({
	saldo,
	saldoInicial,
	flujoNeto
}: {
	saldo: number;
	saldoInicial: number;
	flujoNeto: number;
}): Rentabilidad | null {
	const base = saldoInicial + Math.max(flujoNeto, 0);
	if (base <= 0) return null;
	const ganancia = saldo - saldoInicial - flujoNeto;
	const porcentaje = (ganancia / base) * 100;
	if (Math.abs(porcentaje) > MAXIMO_CREIBLE) return null;
	return { ganancia, base, porcentaje };
}

/**
 * La rentabilidad de varios planes juntos: euros y bases se suman y el % se calcula una sola vez
 * (promediar porcentajes daría el mismo peso a un plan de 100 € que a uno de 30.000 €).
 *
 * 🔴 Si de algún plan no hay dato, del total tampoco: un total con un plan de menos se leería
 * como el de todos.
 */
export function rentabilidadAgregada(planes: (Rentabilidad | null)[]): Rentabilidad | null {
	if (planes.length === 0 || planes.some((plan) => plan === null)) return null;
	const conocidos = planes as Rentabilidad[];
	const ganancia = conocidos.reduce((total, plan) => total + plan.ganancia, 0);
	const base = conocidos.reduce((total, plan) => total + plan.base, 0);
	// La base es lo metido, y un plan solo es fiable con lo metido > 0 (`calculatePensionPlanMetrics`):
	// la suma nunca es 0.
	return { ganancia, base, porcentaje: (ganancia / base) * 100 };
}
